// The callback half of the OAuth round trip: the route Accounts redirects back to, holding an
// authorization code worth exactly one redemption. Arrived at and never linked to — it draws the
// same card the trip started from, turned to say how the trip is ending, and hands over to the
// drive once there is a session to open it with.
import {useMutation} from '@apollo/client/react'
import {useEffect, useRef, useState} from 'react'
import {Navigate, useNavigate, useSearchParams} from 'react-router-dom'

import AccountsSignIn from './AccountsSignIn'

import {OAUTH_TOKEN_REQUEST} from '../../graphql/mutations/oauth'
import {setItem} from '../../lib/authTools'
import {exchangeBearer} from '../../lib/sessionTools'

// Long enough to read that it worked before the card gives way to the drive.
export const OPENING_DELAY = 800

const SignInContainer = () => {
  const [query] = useSearchParams()
  const navigate = useNavigate()
  const code = query.get('code')
  // Accounts sends the person back without a code when they decline, and says why in its place.
  const declined = !code && query.has('error')
  const [phase, setPhase] = useState(declined ? 'refused' : 'returning')
  const [requestOAuthToken] = useMutation(OAUTH_TOKEN_REQUEST)
  // React runs an effect twice in development. A code redeemed twice is a code refused the second
  // time — and the refusal lands after the token was written, in time to overwrite it.
  const redeemed = useRef(false)

  useEffect(() => {
    if (!code || redeemed.current) return

    redeemed.current = true

    requestOAuthToken({
      variables: {
        input: {
          code,
          state: query.get('state')
        }
      },
      // The grant is a single-use bearer rather than a session, and the card only says it worked
      // once the bearer has been traded for one. Apollo drops the promise this hands back, so
      // every way out of it ends in a phase rather than a throw.
      onCompleted: ({oauthCodeGrant}) => {
        if (!oauthCodeGrant?.authToken) {
          setPhase('refused')

          return
        }

        setItem('authToken', oauthCodeGrant.authToken)
        exchangeBearer().then(traded => setPhase(traded ? 'granted' : 'refused'))
      },
      onError: () => setPhase('refused')
    })
  }, [code])

  useEffect(() => {
    if (phase !== 'granted') return

    const opening = setTimeout(() => navigate('/', {replace: true}), OPENING_DELAY)

    return () => clearTimeout(opening)
  }, [phase])

  // Nobody arriving from Accounts — an old bookmark, or the route typed out. Nothing has gone
  // wrong, so they are sent to where signing in starts.
  if (!code && !declined) return <Navigate to='/' replace />

  return <AccountsSignIn phase={phase} />
}

export default SignInContainer
